import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, CopyIcon, FileTextIcon, MailIcon, NetworkIcon, PhoneIcon, UserIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { treatmentCategories, treatments } from '../data/treatments';
import { clinic, images } from '../data/clinic';
import type { TreatmentCategory } from '../types/care';
const channels = [{
  icon: NetworkIcon,
  title: 'Via Zorgdomein',
  text: 'Zoek op Excellent Care Clinics in Zorgdomein.'
}, {
  icon: MailIcon,
  title: 'Per e-mail',
  text: clinic.email,
  copy: clinic.email
}, {
  icon: UserIcon,
  title: 'Meegeven aan de patiënt',
  text: 'De patiënt neemt de verwijzing zelf mee.'
}];
const process = [{
  title: 'Verwijzing ontvangen',
  text: 'Wij nemen contact op met de patiënt zodra de verwijzing binnen is.'
}, {
  title: 'Intake en behandeling',
  text: 'Binnen twee tot drie weken kan de intake en behandeling plaatsvinden.'
}, {
  title: 'Terugkoppeling',
  text: 'U wordt direct na intake of behandeling op de hoogte gehouden van het verloop en het behandelplan.'
}, {
  title: 'Inzage voor de patiënt',
  text: 'Via een PGO (Persoonlijke Gezondheidsomgeving) heeft de patiënt inzage in de eigen behandeling.'
}];
export function Referrers() {
  const [copied, setCopied] = useState(false);
  const [category, setCategory] = useState<TreatmentCategory>(treatmentCategories[0]);
  const items = treatments.filter(t => t.category === category);
  const copyEmail = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${value}`;
    }
  };
  return <>
      <PageHeader title="Voor verwijzers" crumbs={[{
      label: 'Verwijzers'
    }]} intro="Excellent Care Clinics is een onafhankelijk specialistisch pijnbehandelcentrum. De behandelingen worden uitgevoerd door anesthesioloog-pijnspecialisten en (pijn)verpleegkundigen." data-id="element-663" data-mp="3ml9j7t2agq9u" />

      {/* Refer */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24" data-id="element-664" data-mp="travy4f85lti">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20" data-id="element-665" data-mp="17qiztamc1i41">
          <div data-id="element-666" data-mp="17ru55evfexx9">
            <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-667" data-mp="17o69a92idzfd">Een patiënt verwijzen</h2>
            <p className="mt-4 text-lg leading-relaxed text-muted" data-id="element-668" data-mp="1ho6vc2rritn9">
              Stuur uw verwijzing via een van deze kanalen. Intake en behandeling kunnen binnen twee tot drie weken plaatsvinden.
            </p>
          </div>
          <ul className="divide-y divide-line border-y border-line" data-id="element-669" data-mp="1qfqnu75t479s">
            {channels.map(({
            icon: Icon,
            title,
            text,
            copy
          }) => <li key={title} className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center" data-id="element-670" data-mp="1mhsy9384jj5v">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary" data-id="element-671" data-mp="20d3n9zzota2b">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-672" data-mp="2f26k6hc3dbjf" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-673" data-mp="2k0ztryo9cieb">
                  <h3 className="font-display text-lg font-semibold text-ink" data-id="element-674" data-mp="2jzovgedl02ul">{title}</h3>
                  <p className="mt-0.5 break-words text-[15px] text-muted" data-id="element-675" data-mp="252pg24chn5pk">{text}</p>
                </div>
                {copy && <button type="button" onClick={() => copyEmail(copy)} className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold text-primary ring-1 ring-line transition-colors duration-150 hover:ring-primary sm:self-center" data-id="element-676" data-mp="2a4wtyzgvovuj">
                    {copied ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-677" data-mp="1rcualgcur1gl" /> : <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-678" data-mp="1dctba5c8k238" />}
                    {copied ? 'Gekopieerd' : 'Kopieer'}
                  </button>}
              </li>)}
          </ul>
        </div>
      </section>

      {/* After referral */}
      <section className="bg-ink text-white" data-id="element-679" data-mp="1acsuuc15oqvj">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-20 lg:px-8 lg:py-24" data-id="element-680" data-mp="15blp0iikq1ri">
          <img src={images.consult} alt="Pijnspecialist van Excellent Care Clinics" className="aspect-[4/3] w-full rounded-3xl object-cover" data-id="element-681" data-mp="10jc2zdkkxlui" />
          <div data-id="element-682" data-mp="rl012fe9j1mr">
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-id="element-683" data-mp="rkqivdzglyrm">Na de verwijzing</h2>
            <ol className="mt-10 grid gap-x-10 gap-y-9 sm:grid-cols-2" data-id="element-684" data-mp="mkkp9s9mq26w">
              {process.map((step, i) => <li key={step.title} data-id="element-685" data-mp="hmsvespfc602" data-mpi={i}>
                  <span className="font-display text-sm font-semibold text-cyan" data-id="element-686" data-mp="cn7qm1nkp3ff">Stap {i + 1}</span>
                  <p className="mt-1.5 font-display text-lg font-semibold" data-id="element-687" data-mp="2c01zu63451bg">{step.title}</p>
                  <p className="mt-1.5 text-[15px] leading-relaxed text-white/70" data-id="element-688" data-mp="2717il8ksnktg">{step.text}</p>
                </li>)}
            </ol>
          </div>
        </div>
      </section>

      {/* Treatment offer */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24" data-id="element-689" data-mp="2jjb79ts26pxv">
        <div className="max-w-2xl" data-id="element-690" data-mp="2oajimvcy8ldo">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-691" data-mp="2obkv949m5fxi">Ons behandelaanbod</h2>
          <p className="mt-4 text-lg leading-relaxed text-muted" data-id="element-692" data-mp="2t94ll2jl5j5t">
            {treatments.length} behandelingen. Per behandeling vindt u de werkwijze en de patiëntenfolder.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Categorie" data-id="element-693" data-mp="20lh2xt1vl2lt">
          {treatmentCategories.map(cat => {
          const count = treatments.filter(t => t.category === cat).length;
          const selected = category === cat;
          return <button key={cat} type="button" role="tab" aria-selected={selected} onClick={() => setCategory(cat)} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-[15px] font-medium transition-colors duration-150 ${selected ? 'bg-ink text-white' : 'bg-surface text-ink hover:bg-primary-soft'}`} data-id="element-694" data-mp="3gyfexyylwb9x">
                {cat}
                <span className={`text-sm ${selected ? 'text-white/60' : 'text-muted'}`} data-id="element-695" data-mp="319seb3hitqhc">{count}</span>
              </button>;
        })}
        </div>

        <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-696" data-mp="28l3kzqmpipg5">
          {items.map(t => <li key={t.slug} data-id="element-697" data-mp="2n9pzxr8f9f6u">
              <Link to={`/behandelingen/${t.slug}`} className="group flex h-full items-center justify-between gap-4 rounded-2xl border border-line px-5 py-4 transition-colors duration-150 hover:border-primary" data-id="element-698" data-mp="2nb143s5s4oyh">
                <span className="min-w-0" data-id="element-699" data-mp="1pulyi3sgwq38">
                  <span className="block font-medium text-ink group-hover:text-primary" data-id="element-700" data-mp="1ptu0kmbeeg9s">{t.name}</span>
                  {(t.facts.guidance || t.pdf) && <span className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted" data-id="element-701" data-mp="a39u36cbbq2o">
                      {t.facts.guidance && <span data-id="element-702" data-mp="3rkkr34nyr58y">{t.facts.guidance}</span>}
                      {t.pdf && <span className="inline-flex items-center gap-1" data-id="element-703" data-mp="e06a2oi4tkd">
                          <FileTextIcon className="h-3 w-3" aria-hidden="true" data-id="element-704" data-mp="o2svzhej656j" />
                          Folder
                        </span>}
                    </span>}
                </span>
                <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-705" data-mp="f13121lfwo40" />
              </Link>
            </li>)}
        </ul>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8 lg:pb-24" data-id="element-706" data-mp="2zqsfwem589wp">
        <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-primary-soft px-8 py-10 sm:px-12 md:flex-row md:items-center" data-id="element-707" data-mp="33uqdgkvtlcnn">
          <div data-id="element-708" data-mp="3vevxr1wubehk">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-709" data-mp="3qif6hyucgk09">Overleg over een patiënt?</h2>
            <p className="mt-2 text-muted" data-id="element-710" data-mp="3mg22bev8wu5l">{clinic.hours}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-711" data-mp="3mies2e78w8t7">
            <a href={clinic.phoneHref} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-6 py-3 text-[15px] font-semibold text-white hover:bg-primary-dark" data-id="element-712" data-mp="3hisda0brqkia">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-713" data-mp="e1k19x3q2evh" />
              {clinic.phone}
            </a>
            <a href={`mailto:${clinic.email}`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-ink ring-1 ring-line hover:text-primary" data-id="element-714" data-mp="97xlhw29huvg">
              <MailIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-715" data-mp="wzwblm6ddusn" />
              E-mail
            </a>
          </div>
        </div>
      </section>
    </>;
}